/**
 * Form controls on Ledger tokens: input, textarea, native select, select,
 * combobox, field, checkbox, switch (Toggle), radio group and segment group
 * (SegmentedControl).
 *
 * One contract for every text-entry control: 28px tall (24 compact, 32
 * large), radius 3, `--color-border-input` at rest, accent border plus the
 * focus ring when focused, err border when invalid, surface-3 fill when
 * disabled. Chakra's size names stay, so no call site changes: `2xs`/`xs` are
 * 24, `sm`/`md` 28, `lg` and up 32.
 *
 * Spec: docs/design-system/DESIGN-SYSTEM.md, "Components 2: forms".
 */
import { defineRecipe, defineSlotRecipe } from "@chakra-ui/react";
import {
  compactPressArea,
  controlHeight,
  fieldControl,
  floatingList,
  focusRing,
  focusRingWithin,
  listOption,
} from "./shared";

/** Font size and inline padding for a control height. */
const controlText = (height: keyof typeof controlHeight) => ({
  fontSize:
    height === "2xs" || height === "xs" ? "var(--text-12)" : "var(--text-13)",
  textStyle: "none",
  px: "var(--space-2)",
});

/** A 32px press area drawn by an invisible centred box around a small control. */
const hitArea = {
  position: "relative",
  _after: {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "max(100%, var(--hit-min))",
    height: "max(100%, var(--hit-min))",
    transform: "translate(-50%, -50%)",
  },
} as const;

const inputSizes = Object.fromEntries(
  (Object.keys(controlHeight) as Array<keyof typeof controlHeight>).map(
    (name) => [
      name,
      { ...controlText(name), "--input-height": controlHeight[name] },
    ],
  ),
);

export const inputRecipe = defineRecipe({
  base: {
    ...fieldControl,
    focusVisibleRing: "none",
  },
  variants: {
    variant: {
      outline: fieldControl,
      subtle: { ...fieldControl, background: "var(--color-surface-2)" },
      flushed: {
        background: "transparent",
        borderWidth: "0",
        borderBottomWidth: "1px",
        borderRadius: "0",
        px: "0",
      },
    },
    size: inputSizes,
  },
  defaultVariants: {
    size: "sm",
  },
});

const textareaSizes = Object.fromEntries(
  (Object.keys(controlHeight) as Array<keyof typeof controlHeight>).map(
    (name) => [name, { ...controlText(name), py: "var(--space-1)" }],
  ),
);

export const textareaRecipe = defineRecipe({
  base: {
    ...fieldControl,
    focusVisibleRing: "none",
    // Three rows at rest; a caller's `autoresize` grows it to its max and then
    // it scrolls.
    minHeight: "calc(3lh + var(--space-2))",
  },
  variants: {
    variant: {
      outline: fieldControl,
      subtle: { ...fieldControl, background: "var(--color-surface-2)" },
    },
    size: textareaSizes,
  },
});

const nativeSelectSizes = Object.fromEntries(
  (Object.keys(controlHeight) as Array<keyof typeof controlHeight>).map(
    (name) => [
      name,
      {
        root: { "--select-field-height": controlHeight[name] },
        field: {
          ...controlText(name),
          ps: "var(--space-2)",
          pe: "var(--space-6)",
        },
        indicator: { insetEnd: "var(--space-2)", fontSize: "var(--text-14)" },
      },
    ],
  ),
);

export const nativeSelectSlotRecipe = defineSlotRecipe({
  slots: ["root", "field", "indicator"],
  base: {
    // The select stays drawn at its 24 or 28px, aligned with the inputs
    // beside it. Below 1024 its root (a <select> draws no ::after) draws
    // P-20's centred 32px press area, behind the select so presses on the
    // select still reach it; a press on the part outside the select lands on
    // the root, and `useNativeSelectPressArea` forwards it to the select.
    root: {
      ...compactPressArea,
      isolation: "isolate",
      _after: { ...compactPressArea._after, zIndex: -1 },
    },
    field: {
      ...fieldControl,
      focusVisibleRing: "none",
      "& > option, & > optgroup": { background: "var(--color-surface)" },
    },
    indicator: {
      color: "var(--color-fg-3)",
      _disabled: { color: "var(--color-fg-disabled)", opacity: 1 },
      _invalid: { color: "var(--color-err)" },
    },
  },
  variants: {
    variant: {
      outline: { field: fieldControl },
      subtle: {
        field: { ...fieldControl, background: "var(--color-surface-2)" },
      },
    },
    size: nativeSelectSizes,
  },
});

/** Shared by Select and Combobox: the floating list and its rows. */
const listSlots = {
  content: floatingList,
  item: listOption,
  itemGroupLabel: {
    fontSize: "var(--text-12)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-fg-3)",
    paddingInline: "var(--space-2)",
    paddingBlock: "var(--space-1)",
  },
  itemIndicator: { color: "var(--color-accent)" },
} as const;

const selectSizes = Object.fromEntries(
  (["xs", "sm", "md", "lg"] as const).map((name) => [
    name,
    {
      root: {
        "--select-trigger-height": controlHeight[name],
        "--select-trigger-padding-x": "var(--space-2)",
      },
      trigger: { ...controlText(name), px: "var(--select-trigger-padding-x)" },
      content: { padding: "var(--space-1)", gap: "0" },
      item: listOption,
      indicator: {
        _icon: { width: "var(--icon-sm)", height: "var(--icon-sm)" },
      },
    },
  ]),
);

export const selectSlotRecipe = defineSlotRecipe({
  slots: [
    "root",
    "trigger",
    "indicatorGroup",
    "indicator",
    "content",
    "item",
    "itemText",
    "itemIndicator",
    "itemGroup",
    "itemGroupLabel",
    "label",
    "valueText",
    "clearTrigger",
    "control",
  ],
  base: {
    trigger: {
      ...fieldControl,
      // A 24 or 28px trigger presses at 32px below 1024, as buttons do (P-20).
      ...compactPressArea,
      cursor: "pointer",
      focusVisibleRing: "none",
      _expanded: { borderColor: "var(--color-accent)" },
      // The select's placeholder is real text (not ::placeholder), so it is
      // held to 4.5:1: fg-3 rather than the 3.6:1 placeholder token.
      _placeholderShown: { color: "var(--color-fg-3)" },
    },
    indicator: {
      color: "var(--color-fg-3)",
      _disabled: { color: "var(--color-fg-disabled)" },
      _invalid: { color: "var(--color-err)" },
    },
    label: {
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-medium)",
      textStyle: "none",
    },
    ...listSlots,
  },
  variants: {
    variant: {
      outline: { trigger: fieldControl },
      subtle: {
        trigger: { ...fieldControl, background: "var(--color-surface-2)" },
      },
      ghost: {
        trigger: {
          background: "transparent",
          borderColor: "transparent",
          _hover: { background: "var(--color-surface-3)" },
          _expanded: { background: "var(--color-surface-3)" },
        },
      },
    },
    size: selectSizes,
  },
});

const comboboxSizes = Object.fromEntries(
  (["xs", "sm", "md", "lg"] as const).map((name) => [
    name,
    {
      root: {
        "--combobox-input-height": controlHeight[name],
        "--combobox-input-padding-x": "var(--space-2)",
        "--combobox-indicator-size": "var(--icon-sm)",
      },
      input: controlText(name),
      content: {
        "--combobox-item-padding-x": "var(--space-2)",
        "--combobox-item-padding-y": "0",
        "--combobox-indicator-size": "var(--icon-sm)",
      },
    },
  ]),
);

export const comboboxSlotRecipe = defineSlotRecipe({
  slots: [
    "root",
    "label",
    "input",
    "control",
    "trigger",
    "clearTrigger",
    "content",
    "item",
    "itemText",
    "itemIndicator",
    "itemGroup",
    "itemGroupLabel",
    "empty",
    "indicatorGroup",
  ],
  base: {
    input: {
      ...fieldControl,
      focusVisibleRing: "none",
    },
    // A 14px chevron button: pressable across a 32px square.
    trigger: { ...hitArea, color: "var(--color-fg-3)" },
    empty: {
      fontSize: "var(--text-13)",
      color: "var(--color-fg-3)",
      paddingInline: "var(--space-2)",
    },
    ...listSlots,
  },
  variants: {
    variant: {
      outline: { input: fieldControl },
      subtle: {
        input: { ...fieldControl, background: "var(--color-surface-2)" },
      },
    },
    size: comboboxSizes,
  },
});

export const fieldSlotRecipe = defineSlotRecipe({
  slots: ["root", "label", "requiredIndicator", "helperText", "errorText"],
  base: {
    root: { gap: "var(--space-1)" },
    label: {
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-medium)",
      color: "var(--color-fg)",
      textStyle: "none",
      _disabled: { color: "var(--color-fg-disabled)", opacity: 1 },
    },
    requiredIndicator: { color: "var(--color-err)" },
    helperText: {
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-regular)",
      color: "var(--color-fg-3)",
      textStyle: "none",
    },
    errorText: {
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-medium)",
      color: "var(--color-err)",
      textStyle: "none",
      gap: "var(--space-1)",
      _icon: { width: "var(--icon-sm)", height: "var(--icon-sm)" },
    },
  },
});

/** The 14px box shared by Checkbox and the checkbox inside other controls. */
const checkControl = {
  ...hitArea,
  boxSize: "var(--icon-sm)",
  padding: "0",
  borderWidth: "1px",
  borderColor: "var(--color-border-input)",
  borderRadius: "var(--radius-1)",
  background: "var(--color-surface)",
  color: "var(--color-accent-fg)",
  cursor: "pointer",
  focusVisibleRing: "none",
  _focusVisible: focusRing,
  "&:is([data-state=checked], [data-state=indeterminate])": {
    background: "var(--color-accent)",
    borderColor: "var(--color-accent)",
    color: "var(--color-accent-fg)",
  },
  _invalid: { borderColor: "var(--color-err)" },
  _disabled: {
    opacity: 1,
    cursor: "not-allowed",
    background: "var(--color-surface-3)",
    borderColor: "var(--color-border)",
    color: "var(--color-fg-disabled)",
    "&:is([data-state=checked], [data-state=indeterminate])": {
      background: "var(--color-surface-3)",
      borderColor: "var(--color-border)",
      color: "var(--color-fg-disabled)",
    },
  },
} as const;

const checkLabel = {
  fontSize: "var(--text-13)",
  fontWeight: "var(--weight-regular)",
  color: "var(--color-fg)",
  textStyle: "none",
  cursor: "pointer",
  _disabled: {
    opacity: 1,
    color: "var(--color-fg-disabled)",
    cursor: "not-allowed",
  },
} as const;

const checkboxSize = {
  root: { gap: "var(--space-2)" },
  control: { boxSize: "var(--icon-sm)", padding: "0" },
  label: { fontSize: "var(--text-13)", textStyle: "none" },
};

export const checkboxSlotRecipe = defineSlotRecipe({
  slots: ["root", "control", "label", "indicator"],
  base: {
    root: { gap: "var(--space-2)" },
    control: checkControl,
    label: checkLabel,
  },
  variants: {
    variant: {
      solid: { control: checkControl },
      outline: { control: checkControl },
      subtle: { control: checkControl },
    },
    size: {
      xs: {
        ...checkboxSize,
        label: { fontSize: "var(--text-12)", textStyle: "none" },
      },
      sm: checkboxSize,
      md: checkboxSize,
      lg: {
        ...checkboxSize,
        control: { boxSize: "var(--icon-md)", padding: "0" },
      },
    },
  },
  defaultVariants: {
    // The app's checkboxes have always defaulted to `sm`.
    size: "sm",
  },
});

const switchTrack = {
  background: "var(--color-border-input)",
  _checked: { background: "var(--color-accent)" },
  focusVisibleRing: "none",
  _focusVisible: focusRing,
  _disabled: {
    opacity: 1,
    cursor: "not-allowed",
    background: "var(--color-surface-3)",
    _checked: { background: "var(--color-border-strong)" },
  },
};

const switchThumb = {
  background: "var(--color-surface)",
  width: "var(--switch-height)",
  height: "var(--switch-height)",
  scale: "0.75",
  boxShadow: "none",
  _checked: { background: "var(--color-surface)" },
};

const switchSize = (width: string, height: string) => ({
  root: {
    "--switch-width": width,
    "--switch-height": height,
    "--switch-indicator-font-size": "var(--text-11)",
  },
});

export const switchSlotRecipe = defineSlotRecipe({
  slots: ["root", "control", "thumb", "label", "indicator"],
  base: {
    root: { gap: "var(--space-2)" },
    control: { ...hitArea, ...switchTrack },
    label: { ...checkLabel, lineHeight: "var(--leading-normal)" },
  },
  variants: {
    variant: {
      solid: { control: switchTrack, thumb: switchThumb },
      darkerTrack: { control: switchTrack, thumb: switchThumb },
    },
    size: {
      xs: switchSize("var(--control-sm)", "var(--space-3)"),
      sm: switchSize("var(--control-md)", "var(--space-4)"),
      md: switchSize("var(--control-md)", "var(--space-4)"),
      lg: switchSize("var(--control-lg)", "var(--space-5)"),
    },
  },
});

const radioControl = {
  ...hitArea,
  boxSize: "var(--icon-sm)",
  borderWidth: "1px",
  borderColor: "var(--color-border-input)",
  borderRadius: "var(--radius-pill)",
  background: "var(--color-surface)",
  color: "transparent",
  focusVisibleRing: "none",
  _focusVisible: focusRing,
  _checked: {
    background: "var(--color-surface)",
    borderColor: "var(--color-accent)",
    color: "var(--color-accent)",
  },
  "& .dot": { scale: "0.5" },
  _invalid: { borderColor: "var(--color-err)" },
  _disabled: {
    opacity: 1,
    cursor: "not-allowed",
    background: "var(--color-surface-3)",
    borderColor: "var(--color-border)",
    _checked: { color: "var(--color-fg-disabled)" },
  },
} as const;

export const radiomarkRecipe = defineRecipe({
  base: radioControl,
  variants: {
    variant: { solid: radioControl, outline: radioControl },
  },
});

const radioSize = {
  item: {
    gap: "var(--space-2)",
    fontSize: "var(--text-13)",
    textStyle: "none",
  },
  itemControl: { boxSize: "var(--icon-sm)" },
};

export const radioGroupSlotRecipe = defineSlotRecipe({
  slots: ["root", "item", "itemControl", "itemText", "label", "indicator"],
  base: {
    item: { _disabled: { opacity: 1, cursor: "not-allowed" } },
    itemControl: radioControl,
    itemText: {
      fontSize: "var(--text-13)",
      color: "var(--color-fg)",
      _disabled: { opacity: 1, color: "var(--color-fg-disabled)" },
    },
  },
  variants: {
    variant: {
      solid: { itemControl: radioControl },
      outline: { itemControl: radioControl },
      subtle: { itemControl: radioControl },
    },
    size: { xs: radioSize, sm: radioSize, md: radioSize, lg: radioSize },
  },
});

/** A segment's height inside the 1px border of the group. */
const segmentSize = (height: string) => ({
  root: { "--segment-item-h": `calc(${height} - 2px)` },
  item: {
    height: "var(--segment-item-h)",
    px: "var(--space-3)",
    gap: "var(--space-1)",
    fontSize: "var(--text-13)",
    textStyle: "none",
  },
});

export const segmentGroupSlotRecipe = defineSlotRecipe({
  slots: ["root", "item", "indicator", "itemText", "itemControl", "label"],
  base: {
    root: {
      "--segment-radius": "var(--radius-2)",
      "--segment-indicator-bg": "var(--color-surface)",
      "--segment-indicator-shadow": "none",
      background: "var(--color-surface-2)",
      border: "1px solid",
      borderColor: "var(--color-border)",
      boxShadow: "none",
      // Wraps on a narrow screen rather than overflowing.
      flexWrap: "wrap",
      minW: "0",
      maxW: "100%",
      // Below 1024 every segment takes presses over at least 32px (its
      // centred press area, on the item below). The group stands clear of
      // what is above and below it by the part of that area outside its
      // border, and wrapped rows of segments stand apart by the whole
      // difference, so no two press areas overlap. From 1024 nothing moves.
      marginY: {
        base: "max(0px, calc((var(--hit-min) - var(--segment-item-h)) / 2 - 1px))",
        lg: "0",
      },
      rowGap: {
        base: "max(0px, calc(var(--hit-min) - var(--segment-item-h)))",
        lg: "0",
      },
    },
    item: {
      ...focusRingWithin,
      position: "relative",
      _after: {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "max(100%, var(--hit-min))",
        height: "max(100%, var(--hit-min))",
        transform: "translate(-50%, -50%)",
        display: { base: "block", lg: "none" },
      },
      fontWeight: "var(--weight-medium)",
      color: "var(--color-fg-2)",
      cursor: "pointer",
      transitionProperty: "color",
      transitionDuration: "var(--dur-1)",
      _hover: { color: "var(--color-fg)" },
      _checked: { color: "var(--color-fg)" },
      _before: { background: "var(--color-border-2)" },
      _disabled: {
        opacity: 1,
        color: "var(--color-fg-disabled)",
        cursor: "not-allowed",
      },
      "&[data-state=checked][data-ssr]": {
        shadow: "none",
        background: "var(--color-surface)",
      },
    },
    indicator: {
      border: "1px solid",
      borderColor: "var(--color-border)",
      boxShadow: "none",
    },
  },
  variants: {
    size: {
      xs: segmentSize("var(--control-sm)"),
      sm: segmentSize("var(--control-md)"),
      md: segmentSize("var(--control-md)"),
      lg: segmentSize("var(--control-lg)"),
    },
  },
});
